import baseAPI from "@/api/base";
import { AxiosResponse } from "axios";
import { CRM_CONNECTIONS_URL, CRM_CONNECT_URL, CRM_SYNC_PREFERENCES_URL } from "@/utils/urls";

// --------------- Field options ---------------
export interface SyncFieldDefinition {
  key: string;
  sbLabel: string;
  hsLabel: string;
  crmLabels?: Record<string, string>;
  defaultEnabled: boolean;
  required?: boolean;
}

export function getCrmLabel(field: SyncFieldDefinition, provider?: string | null): string {
  if (provider && field.crmLabels?.[provider]) {
    return field.crmLabels[provider];
  }
  return field.hsLabel;
}

export const SYNC_FIELD_DEFINITIONS: SyncFieldDefinition[] = [
  {
    key: "contact_name",
    sbLabel: "First & Last Name",
    hsLabel: "First Name / Last Name",
    crmLabels: { pipedrive: "Name", salesforce: "FirstName / LastName" },
    defaultEnabled: true,
    required: true,
  },
  {
    key: "contact_email",
    sbLabel: "Email",
    hsLabel: "Email",
    crmLabels: { pipedrive: "Email (array)", salesforce: "Email" },
    defaultEnabled: true,
    required: true,
  },
  {
    key: "contact_additional_emails",
    sbLabel: "Additional Emails",
    hsLabel: "Additional Emails",
    crmLabels: { pipedrive: "Email (array)" },
    defaultEnabled: true,
  },
  {
    key: "contact_phone",
    sbLabel: "Phone Numbers",
    hsLabel: "Phone / Mobile / Home",
    crmLabels: { pipedrive: "Phone (array)", salesforce: "Phone / MobilePhone" },
    defaultEnabled: true,
  },
  {
    key: "contact_jobtitle",
    sbLabel: "Job Title",
    hsLabel: "Job Title",
    crmLabels: { pipedrive: "Job Title", salesforce: "Title" },
    defaultEnabled: true,
  },
  {
    key: "contact_linkedin",
    sbLabel: "LinkedIn URL",
    hsLabel: "LinkedIn",
    crmLabels: { pipedrive: "LinkedIn URL (custom)", salesforce: "SB_LinkedIn__c" },
    defaultEnabled: true,
  },
  {
    key: "contact_company",
    sbLabel: "Company Info",
    hsLabel: "Company / Website / Industry",
    crmLabels: { pipedrive: "Organization" },
    defaultEnabled: true,
  },
  {
    key: "contact_timezone",
    sbLabel: "Timezone",
    hsLabel: "Timezone",
    crmLabels: { pipedrive: "Timezone", salesforce: "SB_Timezone__c" },
    defaultEnabled: true,
  },
  {
    key: "contact_tags",
    sbLabel: "Tags",
    hsLabel: "SalesBuckets Tags",
    crmLabels: { pipedrive: "Tags (custom)", salesforce: "SB_Tags__c" },
    defaultEnabled: true,
  },
  {
    key: "contact_lead_status",
    sbLabel: "Call Disposition",
    hsLabel: "Lead Status",
    crmLabels: { pipedrive: "Lead Status" },
    defaultEnabled: true,
  },
  {
    key: "contact_lifecycle_stage",
    sbLabel: "Lifecycle Stage",
    hsLabel: "Lifecycle Stage",
    crmLabels: { pipedrive: "Lifecycle Stage" },
    defaultEnabled: true,
  },
  {
    key: "contact_email_status",
    sbLabel: "Email Verification",
    hsLabel: "Email Status",
    crmLabels: { pipedrive: "Email Status", salesforce: "SB_Email_Status__c" },
    defaultEnabled: true,
  },
  {
    key: "contact_created_date",
    sbLabel: "Created Date",
    hsLabel: "Created Date",
    crmLabels: { pipedrive: "Created Date", salesforce: "SB_Created_Date__c" },
    defaultEnabled: false,
  },
  {
    key: "contact_owner",
    sbLabel: "Owner",
    hsLabel: "Owner (HubSpot Owner)",
    crmLabels: { pipedrive: "Owner (custom)", salesforce: "SB_Owner__c" },
    defaultEnabled: true,
  },
  {
    key: "contact_bucket",
    sbLabel: "Bucket",
    hsLabel: "SalesBuckets Bucket",
    crmLabels: { pipedrive: "Bucket (custom)" },
    defaultEnabled: true,
  },
  {
    key: "contact_last_activity_at",
    sbLabel: "Last Activity",
    hsLabel: "SalesBuckets Last Activity",
    crmLabels: { pipedrive: "Last Activity" },
    defaultEnabled: true,
  },
  {
    key: "contact_is_active",
    sbLabel: "Active Status",
    hsLabel: "SalesBuckets Active",
    crmLabels: { pipedrive: "Active" },
    defaultEnabled: true,
  },
  {
    key: "contact_added_by",
    sbLabel: "Added By",
    hsLabel: "SalesBuckets Added By",
    crmLabels: { pipedrive: "Added By" },
    defaultEnabled: false,
  },
];

// Which field keys are considered "recommended" defaults. Used by the
// Reset-to-recommended button in CrmConfigureModal.
export const RECOMMENDED_FIELD_DEFAULTS: Record<string, boolean> = Object.fromEntries(
  SYNC_FIELD_DEFINITIONS.map((f) => [f.key, f.defaultEnabled])
);

export const RECOMMENDED_ACTIVITY_DEFAULTS: Record<string, boolean> = {
  syncNotes: true,
  syncMeetings: true,
  syncCallRecords: true,
  syncAICallAnalysis: true,
  syncMovementHistory: true,
  syncEmails: true,
  syncSMS: false,
  syncContacts: true,
  syncDeals: true,
  syncTranscripts: true,
};

// --------------- Service class ---------------
class CrmIntegration {
  static getConnections = async (): Promise<AxiosResponse> => {
    return baseAPI.get(CRM_CONNECTIONS_URL);
  };

  static connectProvider = async (provider: string): Promise<AxiosResponse> => {
    return baseAPI.get(`${CRM_CONNECT_URL}/${provider}/connect`, {
      params: { redirectBack: window.location.origin },
    });
  };

  static disconnectProvider = async (provider: string): Promise<AxiosResponse> => {
    return baseAPI.delete(`${CRM_CONNECT_URL}/${provider}/disconnect`);
  };

  static getConfiguration = async (provider: string): Promise<AxiosResponse> => {
    return baseAPI.get(`${CRM_CONNECT_URL}/${provider}/config`);
  };

  static saveConfiguration = async (provider: string, config: any): Promise<AxiosResponse> => {
    return baseAPI.put(`${CRM_CONNECT_URL}/${provider}/config`, config);
  };

  static getSyncPreferences = async (provider?: string): Promise<AxiosResponse> => {
    const url = provider
      ? `${CRM_CONNECT_URL}/${provider}/sync-preferences`
      : CRM_SYNC_PREFERENCES_URL;
    return baseAPI.get(url);
  };

  static updateSyncPreferences = async (prefs: any, provider?: string): Promise<AxiosResponse> => {
    const url = provider
      ? `${CRM_CONNECT_URL}/${provider}/sync-preferences`
      : CRM_SYNC_PREFERENCES_URL;
    return baseAPI.put(url, { syncPreferences: prefs });
  };

  /** @deprecated prefer syncContacts (async job + polling). Kept for non-HubSpot providers. */
  static syncAll = async (provider: string): Promise<AxiosResponse> => {
    return baseAPI.post(`${CRM_CONNECT_URL}/${provider}/sync/all`);
  };

  /**
   * HubSpot-only: enqueue a contact-only sync and return 202 with { jobId }.
   * The frontend polls getJobStatus for progress.
   */
  static syncContacts = async (): Promise<AxiosResponse> => {
    return baseAPI.post(`${CRM_CONNECT_URL}/hubspot/sync/contacts`, undefined, { timeout: 60_000 });
  };

  static getJobStatus = async (jobId: string): Promise<AxiosResponse> => {
    return baseAPI.get(`${CRM_CONNECT_URL}/hubspot/jobs/${jobId}`);
  };

  static getSyncStatus = async (provider: string): Promise<AxiosResponse> => {
    return baseAPI.get(`${CRM_CONNECT_URL}/${provider}/sync/status`);
  };

  static getPipelineStages = async (provider: string): Promise<AxiosResponse> => {
    return baseAPI.get(`${CRM_CONNECT_URL}/${provider}/pipeline-stages`);
  };

  static getPipelines = async (refresh = false): Promise<AxiosResponse> => {
    return baseAPI.get(`${CRM_CONNECT_URL}/hubspot/pipelines`, {
      params: refresh ? { refresh: 1 } : undefined,
    });
  };

  static getOwners = async (refresh = false): Promise<AxiosResponse> => {
    return baseAPI.get(`${CRM_CONNECT_URL}/hubspot/owners`, {
      params: refresh ? { refresh: 1 } : undefined,
    });
  };

  static syncOwners = async (): Promise<AxiosResponse> => {
    return baseAPI.post(`${CRM_CONNECT_URL}/hubspot/owners/sync`);
  };

  static getOpportunityStagesForMapping = async (): Promise<AxiosResponse> => {
    return baseAPI.get(`${CRM_CONNECT_URL}/hubspot/opportunity-stages`);
  };

  static getBucketOptionsForMapping = async (): Promise<AxiosResponse> => {
    return baseAPI.get(`${CRM_CONNECT_URL}/hubspot/bucket-options`);
  };

  static getLifecycleOptions = async (): Promise<AxiosResponse> => {
    return baseAPI.get(`${CRM_CONNECT_URL}/hubspot/lifecycle-options`);
  };
}

// --------------- Shared types for UI dropdowns ---------------
export interface HubSpotPipelineStage {
  id: string;
  label: string;
  displayOrder: number;
}
export interface HubSpotPipeline {
  id: string;
  label: string;
  stages: HubSpotPipelineStage[];
}
export interface HubSpotOwner {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
}
export interface MappingOpportunityStage {
  id: string;
  name: string;
  slug: string;
}
export interface MappingBucketOption {
  id: string;
  name: string;
  slug: string;
  color?: string;
}
export interface LifecycleOption {
  value: string;
  label: string;
}

export default CrmIntegration;
